import axios, { type AxiosInstance } from 'axios';

import AuthTokenRequestInterceptor from '@rygen/rygen-ui/api/interceptor/AuthTokenRequestInterceptor';
import { errorHandlingInterceptorInstaller } from '@rygen/rygen-ui/api/interceptor/ErrorHandlingInterceptor';
import ImpersonateUserErrorResponseInterceptor from '@rygen/rygen-ui/api/interceptor/ImpersonateUserErrorResponseInterceptor';
import ImpersonateUserRequestInterceptor from '@rygen/rygen-ui/api/interceptor/ImpersonateUserRequestInterceptor';

import AbortControllerRequestInterceptor from '@/api/interceptor/AbortControllerRequestInterceptor';
import ApplicationVersionRequestInterceptor from '@/api/interceptor/ApplicationVersionRequestInterceptor';
import ApplicationVersionResponseInterceptor from '@/api/interceptor/ApplicationVersionResponseInterceptor';
import DomainContextRequestInterceptor from '@/api/interceptor/DomainContextRequestInterceptor';

export interface BaseHttpControllerOptions {
  /**
   * Indicates that requests in this controller should not pass an
   * `Authorization` header
   */
  bypassAuthInterceptor?: boolean;
  /**
   * Indicates that requests in this controller should return results for the
   * indicated domain in the domain header, as well as its subdomains.
   *
   * Defaults to `false`, which means results will only include the indicated
   * domain.
   */
  canIncludeSubDomains?: boolean;
  /**
   * Indicates that requests in this controller should always use the
   * selected domain and ignore the parent entity owner.
   *
   * Defaults to `false`, which means requests will be sent using the domain
   * context of the parent entity owner, if present.
   */
  useSelectedDomainContext?: boolean;
  /**
   * Indicates that requests in this controller should persists across route
   * navigations.
   *
   * Defaults to `false`, which means requests will be canceled if the user
   * navigates away from the calling route.
   */
  allowNavigations?: boolean;
}

const DEFAULT_OPTIONS = {
  bypassAuthInterceptor: false,
  canIncludeSubDomains: false,
  useSelectedDomainContext: false,
  allowNavigations: false,
};

export class BaseHttpController {
  http: AxiosInstance;

  constructor(endpoint = '', options: BaseHttpControllerOptions = {}) {
    const resolvedOptions: Required<BaseHttpControllerOptions> = {
      ...DEFAULT_OPTIONS,
      ...options,
    };

    this.http = axios.create({
      // False positive from semgrep: `endpoint` is a hardcoded string literal
      // supplied by each controller subclass (e.g. 'shipments', 'orders') —
      // never user-supplied at runtime. `BASE_URL` is a Vite build-time env
      // variable. Semgrep flags this because the baseURL is constructed from a
      // template literal, but neither interpolated value originates from user
      // input.
      // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf
      baseURL: `${import.meta.env.BASE_URL}api/${endpoint}`,
    });

    if (!resolvedOptions.bypassAuthInterceptor) {
      this.http.interceptors.request.use(AuthTokenRequestInterceptor);
    }

    if (!resolvedOptions.allowNavigations) {
      this.http.interceptors.request.use(AbortControllerRequestInterceptor);
    }

    this.http.interceptors.request.use((axiosRequestConfig) =>
      DomainContextRequestInterceptor(
        axiosRequestConfig,
        resolvedOptions.canIncludeSubDomains,
        resolvedOptions.useSelectedDomainContext,
      ),
    );

    this.http.interceptors.request.use(ImpersonateUserRequestInterceptor);
    this.http.interceptors.request.use(ApplicationVersionRequestInterceptor);
    this.http.interceptors.response.use(ApplicationVersionResponseInterceptor);
    this.http.interceptors.response.use(
      (value) => value,
      ImpersonateUserErrorResponseInterceptor,
    );
    errorHandlingInterceptorInstaller.install(this.http);
  }
}
